/**
 * Header Component (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Header section
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Icon } from '@myvault/shared/components';
import { UserMenu } from './UserMenu';
import { useSearchModal } from '../../features/search';

interface HeaderProps {
  onMenuToggle: () => void;
  onNotificationsToggle: () => void;
  notificationCount: number;
}

export function Header({ onMenuToggle, onNotificationsToggle, notificationCount }: HeaderProps) {
  const { openSearch } = useSearchModal();
  const [showSecurityTooltip, setShowSecurityTooltip] = useState(false);

  return (
    <header className="sticky top-0 z-50 h-16 border-b border-gray-200 bg-blue-900">
      <div className="flex h-full items-center justify-between px-4">
        {/* Left: Menu toggle + Logo + Security Indicator */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="rounded p-2 text-white hover:bg-blue-800 lg:hidden"
            aria-label="Open menu"
          >
            <Icon name="menu" variant="outline" size="md" />
          </button>

          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-amber-500">
              <span className="text-sm font-bold text-blue-900">mV</span>
            </div>
            <span className="hidden text-lg font-semibold text-white sm:block">myVault</span>
          </Link>

          {/* Security/Trust Indicator - JM-001 Task 1.4 */}
          <div className="relative hidden sm:block">
            <Link
              to="/security"
              className="flex items-center gap-1 rounded-full bg-green-600/20 px-2 py-1 text-green-400 transition-colors hover:bg-green-600/30"
              onMouseEnter={() => setShowSecurityTooltip(true)}
              onMouseLeave={() => setShowSecurityTooltip(false)}
              onFocus={() => setShowSecurityTooltip(true)}
              onBlur={() => setShowSecurityTooltip(false)}
              aria-label="Je gegevens zijn versleuteld - Meer informatie"
            >
              <LockClosedIcon className="h-3.5 w-3.5" />
              <span className="hidden text-xs font-medium lg:inline">Beveiligd</span>
            </Link>
            {/* Tooltip */}
            {showSecurityTooltip && (
              <div
                role="tooltip"
                className="absolute left-0 top-full z-50 mt-2 w-48 rounded-lg bg-gray-900 p-2 text-xs text-white shadow-lg"
              >
                <p className="font-medium">Je gegevens zijn versleuteld</p>
                <p className="mt-1 text-gray-300">Klik voor meer info over beveiliging</p>
                <div className="absolute -top-1 left-4 h-2 w-2 rotate-45 bg-gray-900" />
              </div>
            )}
          </div>
        </div>

        {/* Center: Search (desktop only) */}
        <div className="hidden flex-1 justify-center px-8 lg:flex">
          <button
            className="flex w-full max-w-md items-center gap-2 rounded-lg border border-blue-700 bg-blue-800 px-4 py-2 text-left text-blue-200 transition-colors hover:bg-blue-700"
            onClick={openSearch}
          >
            <Icon name="search" variant="outline" size="sm" />
            <span className="flex-1">Zoeken in documenten...</span>
            <kbd className="rounded bg-blue-700 px-2 py-0.5 text-xs text-blue-200">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          {/* Search (mobile) */}
          <button
            className="rounded p-2 text-white hover:bg-blue-800 lg:hidden"
            aria-label="Zoeken"
            onClick={openSearch}
          >
            <Icon name="search" variant="outline" size="md" />
          </button>

          {/* Marketplace */}
          <Link
            to="/marketplace"
            className="hidden items-center gap-1.5 rounded-lg px-3 py-1.5 text-white transition-colors hover:bg-blue-800 sm:flex"
            aria-label="Marketplace"
          >
            <Icon name="buildingStorefront" variant="outline" size="sm" />
            <span className="text-sm font-medium">Marketplace</span>
          </Link>

          {/* Help */}
          <button
            className="hidden rounded p-2 text-white hover:bg-blue-800 sm:block"
            aria-label="Help"
          >
            <Icon name="questionMarkCircle" variant="outline" size="md" />
          </button>

          {/* Notifications */}
          <button
            onClick={onNotificationsToggle}
            className="relative rounded p-2 text-white hover:bg-blue-800"
            aria-label={`Notificaties - ${notificationCount} ongelezen`}
          >
            <Icon name="bell" variant="outline" size="md" />
            {notificationCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-medium text-white">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>

          {/* User Menu */}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
